if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (this.openedBy) this.openedBy.setAttribute('aria-expanded', 'false');
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.setAttribute('aria-expanded', 'true');
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.enhanceLoadedContent();

            const productInfo = this.modalContent.querySelector('product-info');
            if (productInfo && window.SmileProductPoints) {
              const variant = window.SmileProductPoints.getVariantFromContainer(productInfo);
              window.SmileProductPoints.sync(productInfo, variant);
            }
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.prepareAccessibleTitle(productElement);
        this.prepareDecorativeIcons(productElement);
      }

      prepareAccessibleTitle(productElement) {
        const titleBlock = productElement.querySelector('.product__title');
        if (!titleBlock) return;

        const headingId = `${this.id}-product-title`;
        const hiddenHeading = titleBlock.querySelector(':scope > h1');
        const titleLink = titleBlock.querySelector(':scope > a');
        const visibleHeading = titleLink?.querySelector('h1, h2, h3');

        if (hiddenHeading) {
          hiddenHeading.setAttribute('aria-hidden', 'true');
          hiddenHeading.removeAttribute('id');
        }

        if (titleLink && visibleHeading) {
          visibleHeading.id = headingId;
          titleLink.replaceWith(visibleHeading);
        } else if (hiddenHeading) {
          hiddenHeading.removeAttribute('aria-hidden');
          hiddenHeading.id = headingId;
        }
      }

      prepareDecorativeIcons(productElement) {
        productElement.querySelectorAll('.product__view-details svg, .share-button svg').forEach((svg) => {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
        });
      }

      enhanceLoadedContent() {
        const dialog = this.querySelector('[role="dialog"]');
        const titleBlock = this.modalContent.querySelector('.product__title');
        const h1 = titleBlock?.querySelector(':scope > h1');
        const heading =
          titleBlock?.querySelector(':scope > h2, :scope > h3') || titleBlock?.querySelector('h2, h3, h1');
        if (h1 && h1 !== heading) {
          h1.setAttribute('aria-hidden', 'true');
          h1.removeAttribute('id');
        }
        if (dialog && heading) {
          heading.id = `${this.id}-product-title`;
          dialog.setAttribute('aria-labelledby', heading.id);
          dialog.removeAttribute('aria-label');
        }

        this.modalContent.querySelectorAll('.jdgm-preview-badge a, .jdgm-preview-badge button, .jdgm-prev-badge__stars').forEach((el) => {
          if (el.getAttribute('aria-label')) return;
          const label = el.textContent.replace(/\s+/g, ' ').trim();
          if (label) el.setAttribute('aria-label', label);
        });

        this.modalContent.querySelectorAll('.ProductSelect_variant').forEach((group) => {
          const label = group.querySelector('.selector-titleho');
          if (label && !group.getAttribute('aria-label')) {
            group.setAttribute('role', 'navigation');
            group.setAttribute('aria-label', label.textContent.replace(/\s+/g, ' ').trim());
          }
          const active = group.querySelector('a.active-optionss, a.active-options');
          if (active) active.setAttribute('aria-current', 'page');
        });

        const closeButton = this.querySelector('.quick-add-modal__toggle');
        if (closeButton) closeButton.focus();
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        const buyTogether = productElement.querySelector('buy-together');
        if (buyTogether) buyTogether.remove();
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
